import { Link } from "react-router-dom";
import { BarChart3, KeyRound, Mail, Power } from "lucide-react";
import { Button } from "@/components/ui/button";
import { type User } from "@/lib/users.api";
import { RolesBadge } from "./RolesBadges";
import { AccountStatusBadge } from "./AccountStatusBadge";
import { employeeDetailUrl } from "../usersPage.helpers";

function initials(name: string): string {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
}

interface UserGridProps {
  users: User[];
  busyId: number | null;
  onEdit: (user: User) => void;
  onResetOtp: (user: User) => void;
  onToggleActivation: (user: User) => void;
}

export function UserGrid({ users, busyId, onEdit, onResetOtp, onToggleActivation }: UserGridProps) {
  if (users.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg px-4 py-8 text-center text-[12px] text-muted-foreground">
        Aucun utilisateur trouvé.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
      {users.map((user) => (
        <div key={user.id} className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-start gap-3">
            <button
              type="button"
              onClick={() => onEdit(user)}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-border bg-surface-muted text-[13px] font-bold text-body"
              title="Modifier l'utilisateur"
            >
              {initials(user.fullName)}
            </button>
            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <button
                    type="button"
                    onClick={() => onEdit(user)}
                    className="truncate text-left text-[13px] font-semibold text-foreground hover:text-brand-gold-dark"
                  >
                    {user.fullName}
                  </button>
                  <p className="mt-0.5 flex items-center gap-1.5 truncate text-[10.5px] text-muted-foreground">
                    <Mail size={11} className="shrink-0 text-subtle" />
                    {user.email}
                  </p>
                </div>
                <AccountStatusBadge active={user.active} firstLogin={user.firstLogin} />
              </div>
              <div className="mt-2">
                <RolesBadge role={user.role} />
              </div>
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between gap-2 border-t border-border pt-3">
            <Link
              to={employeeDetailUrl(user.id)}
              className="inline-flex h-9 items-center gap-1.5 rounded-md px-2.5 text-[11.5px] font-medium text-brand-gold-dark hover:bg-brand-gold-light/30"
            >
              <BarChart3 size={13} />
              Détail employé
            </Link>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                title="Réinitialiser l'OTP"
                disabled={busyId === user.id}
                onClick={() => onResetOtp(user)}
              >
                <KeyRound size={13} />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                title={user.active ? "Désactiver" : "Activer"}
                disabled={busyId === user.id}
                onClick={() => onToggleActivation(user)}
                className={
                  user.active
                    ? "text-danger-text hover:bg-danger-bg"
                    : "text-success-text hover:bg-success-bg"
                }
              >
                <Power size={13} />
              </Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
